import React, {
  createRef,
  RefObject,
  useEffect,
  useState,
  useRef,
} from "react";

export interface Perk {
  title: string;
  description: string;
  iconSrc: string;
  iconAlt: string;
}

interface Props {
  perks?: Perk[];
  subTitle?: string;
}

export default function CareersTeam({
  perks = [
    {
      title: "Great Benefits",
      description:
        "Paid time off, plus medical & dental coverage to fit any lifestyle.",
      iconSrc:
        "//cdnimg.webstaurantstore.com/uploads/design/2017/5/great-benefits-icon.png",
      iconAlt: "",
    },
    {
      title: "Career Growth",
      description: "Develop skills and grow as a valued member of our team.",
      iconSrc:
        "//cdnimg.webstaurantstore.com/uploads/design/2017/5/marketing-icon.png",
      iconAlt: "",
    },
    {
      title: "Coffee & Snacks",
      description:
        "We love food! Enjoy a steady stream of coffee, snacks, and treats.",
      iconSrc:
        "//cdnimg.webstaurantstore.com/uploads/design/2017/5/cafe-icon.png",
      iconAlt: "",
    },
    {
      title: "Competitive Salary",
      description:
        "We recruit the best and offer competitive compensation and a 401k match.",
      iconSrc:
        "//cdnimg.webstaurantstore.com/uploads/design/2017/5/competitive-salary-icon.png",
      iconAlt: "",
    },
    {
      title: "Work & Play",
      description:
        "Supplement your hard work with volunteer opportunities, monthly luncheons, and more.",
      iconSrc:
        "//cdnimg.webstaurantstore.com/uploads/design/2017/5/breakroom-icon.png",
      iconAlt: "",
    },
  ],
  subTitle = "If we listed every reason, we'd run out of pixels. Here's just a few to tempt you.",
}: Props) {
  const [perkRefs, setPerkRefs] = useState<RefObject<HTMLDivElement>[]>([]);
  const [perkContentRefs, setPerkContentRefs] = useState<
    RefObject<HTMLDivElement>[]
  >([]);

  const initialRenderDone = useRef(false);

  useEffect(() => {
    if (!initialRenderDone.current) {
      setPerkRefs(perks.map(() => createRef<HTMLDivElement>()));
      setPerkContentRefs(perks.map(() => createRef<HTMLDivElement>()));

      initialRenderDone.current = true;
    }
  }, [perks, setPerkRefs, setPerkContentRefs]);

  const onWindowScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const viewportBottom = scrollTop + window.innerHeight;

    perkRefs.forEach((ref, index) => {
      if (
        ref.current &&
        ref.current.getBoundingClientRect().top + scrollTop + 160 <
          viewportBottom
      ) {
        ref.current.classList.add("in-view");
        perkContentRefs[index]?.current?.classList.add("in-view");

        if (index === perkRefs.length - 1) {
          window.onscroll = null;
        }
      }
    });
  };

  window.onscroll = onWindowScroll;

  const renderPerk = (singlePerk: Perk, index: number) => {
    const lineSideClass = index % 2 === 0 ? "right" : "left";

    return (
      <div
        className="careers-team__perk-row clearfix"
        key={singlePerk.title.toLowerCase().replace(" ", "")}
      >
        <div
          className={`careers-team-perk ${lineSideClass}`}
          ref={perkRefs[index]}
        >
          <span className="careers-team__perk-icon">
            <img src={singlePerk.iconSrc} alt={singlePerk.iconAlt} />
          </span>
          <div
            className="careers-team-perk-content"
            ref={perkContentRefs[index]}
          >
            <h3 className="careers-team__perk-title">{singlePerk.title}</h3>
            <div className="careers-team__perk-description">
              {singlePerk.description}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="careers-team">
      <div className="careers-section-title">Team</div>
      <div className="careers-team-content">
        <h2 className="careers-team-spaced">Be a part of our</h2>
        <h2 className="careers-team-big">Team</h2>
        <p className="careers-team-text">{subTitle}</p>
      </div>
      <div className="careers-team-perks">
        <div className="careers-team-perk-container">
          {perks.map(renderPerk)}
        </div>
      </div>
    </div>
  );
}
